import React, { useMemo } from 'react';

import cn from 'classnames';

import { UsageInterval, ComputeUsage } from 'api/usageAPI';
import { formatNumber } from 'utils/String';

import { CommonTabProps } from '../Usage';
import s from '../Usage.module.css';
import UsageChart, { UsageData, sortDate } from '../UsageChart';
import UsageTabLayout from '../UsageTabLayout/UsageTabLayout';

interface ComputeTabProps extends CommonTabProps {
  computeUsage: ComputeUsage[];
}

export default function ComputeTab(props: ComputeTabProps) {
  const { loading, error, interval, startDate, endDate, computeUsage, setUsageInterval, setDateRange } =
    props;

  // Compute chart data
  const chartData = useMemo(() => {
    return sumComputeByDate(computeUsage);
  }, [computeUsage]);

  // Compute monthly totalCompute
  const totalCompute = useMemo(() => {
    return chartData.reduce((sum, usageData) => sum + usageData.val, 0);
  }, [chartData]);

  const handleSetInterval = (interval: UsageInterval) => {
    analytics.track('UsageComputeTab SetInterval', {
      interval,
    });
    setUsageInterval(interval);
  };

  const handleSetDateRange = (startDate: string, endDate: string) => {
    analytics.track('UsageComputeTab SetDateRange', {
      startDate,
      endDate,
    });
    setDateRange(startDate, endDate);
  };

  let zeroText = '';
  let headerBox = null;
  let chart = null;

  if (chartData.length) {
    headerBox = (
      <div className={cn(s.colorlessHeaderBox, 'bg-sec-blue-light-50', 'text-sec-blue-light-700')}>
        <div className={s.totalRow}>
          <h4>Total credits used during date range:</h4>
          <div className={s.total}>{formatNumber(totalCompute)}</div>
        </div>
        <a
          href="https://help.mozartdata.com/docs/usage#compute-usage"
          target="blank"
          data-track="Usage LinkToComputeHelp"
          className={s.helpLink}
        >
          How Compute Works
        </a>
      </div>
    );

    chart = (
      <UsageChart
        interval={interval}
        yAxisLabel="Credits"
        hoverLabel="Compute"
        usageData={chartData}
        color={getComputedStyle(document.documentElement).getPropertyValue('--sec-blue-light-700')}
      />
    );
  } else {
    zeroText = 'Zero credits used during the selected date range.';
  }

  return (
    <UsageTabLayout
      loading={loading}
      error={error}
      zeroText={zeroText}
      interval={interval}
      startDate={startDate}
      endDate={endDate}
      setUsageInterval={handleSetInterval}
      setDateRange={handleSetDateRange}
      headerBox={headerBox}
      chart={chart}
      table={null}
      tabType="compute"
    />
  );
}

// Calculate the total compute for every date.
function sumComputeByDate(usage: ComputeUsage[]) {
  const sumDic: { [key: string]: number } = {};
  usage.forEach((u) => {
    if (!sumDic[u.measured_date]) {
      sumDic[u.measured_date] = 0;
    }
    sumDic[u.measured_date] += u.compute_credits;
  });

  // Get list of sorted dates
  const sortedDates = Object.keys(sumDic).sort(sortDate);

  // Convert to the format the UsageChart wants.
  const graphData: UsageData[] = sortedDates.map((k) => ({ date: k, val: sumDic[k] }));
  return graphData;
}
